import mangaDexProfile from "../../repositories/yomiri/mangadex.json";
import leerProfile from "../../repositories/yomiri/leercapitulo.json";
import kazokuProfile from "../../repositories/yomiri/kazoku-den.json";
import anzProfile from "../../repositories/yomiri/anzmanga.json";
import dbNewHopeProfile from "../../repositories/yomiri/dbnewhope.json";
import marmotaProfile from "../../repositories/yomiri/marmota.json";
import { DeclarativeSource } from "./DeclarativeSource.ts";
import { MangaDex } from "./connectors/MangaDex.ts";
import { DbNewHope } from "./connectors/DbNewHope.ts";
import { sourceHttpClient, sourceExtensionTransport } from "../core/SourceHttp.ts";
import { registerDynamicSource, unregisterDynamicSource } from "../network/SourceRegistry.ts";
import { validateProfile } from "./ProfileValidator.ts";
import { detectSourceProfile, InstallationError } from "../repositories/TemplateDetector.ts";
import type { RepositoryEntry } from "../repositories/RepositoryStore.ts";
import type { SourceProfile } from "./types.ts";
import type { YomiriExtension } from "../types/extension.ts";
import { adaptationFor } from '../repositories/AdaptedCatalog.ts';
export interface InstalledProfile {
  entryId: string;
  runtimeId: string;
  repository: string;
  iconUrl: string;
  profile: SourceProfile;
}
export function installedProfiles(): InstalledProfile[] {
  try {
    const records: unknown = JSON.parse(localStorage.getItem("yomiri_installed_profiles") ?? "[]");
    return Array.isArray(records)
      ? records.filter((record) => record && typeof record.runtimeId === "string" && record.profile)
      : [];
  } catch {
    return [];
  }
}
export function restoreRuntimeSources(): YomiriExtension[] {
  const restored: YomiriExtension[] = [];
  for (const record of installedProfiles().slice(0, 128))
    try {
      const profile = validateProfile(record.profile);
      if (!profile.id.startsWith("dynamic-") || profile.id !== record.runtimeId) continue;
      registerDynamicSource({
        id: profile.id,
        revision: "1",
        baseUrl: profile.baseUrl,
        allowedHosts: profile.allowedHosts,
      });
      restored.push(new DeclarativeSource(profile, sourceHttpClient));
    } catch {
      /* Invalid local profiles never authorize hosts. */
    }
  return restored;
}
const installing = new Map<string, Promise<YomiriExtension>>();
export function installRuntimeSource(
  entry: RepositoryEntry,
  native: (id: string) => YomiriExtension | undefined,
): Promise<YomiriExtension> {
  const adaptation = adaptationFor(entry.extension.pkg);
  if (adaptation?.state === 'blocked')
    return Promise.reject(new InstallationError('CHALLENGE_REQUIRED', adaptation.label));
  const existing = entry.nativeId ? native(entry.nativeId) : undefined;
  if (existing) return Promise.resolve(existing);
  if (entry.nativeId === "tmo")
    return Promise.reject(
      new InstallationError(
        "UNSUPPORTED_ENGINE",
        "TMO todavía no pasó la verificación contra zonatmo.com.",
      ),
    );
  const pending = installing.get(entry.entryId);
  if (pending) return pending;
  const operation = detectSourceProfile(
    entry.source,
    entry.extension,
    entry.runtimeId,
    sourceHttpClient,
  )
    .then((profile) => {
      const source = new DeclarativeSource(profile, sourceHttpClient);
      const records = installedProfiles().filter((record) => record.runtimeId !== profile.id);
      records.push({
        entryId: entry.entryId,
        runtimeId: profile.id,
        repository: entry.repository,
        iconUrl: entry.extension.iconUrl,
        profile,
      });
      try {
        localStorage.setItem("yomiri_installed_profiles", JSON.stringify(records));
      } catch (error) {
        unregisterDynamicSource(profile.id);
        throw error;
      }
      return source;
    })
    .catch(async (error) => {
      await sourceExtensionTransport.unregisterSource(entry.runtimeId).catch(() => undefined);
      throw error;
    })
    .finally(() => installing.delete(entry.entryId));
  installing.set(entry.entryId, operation);
  return operation;
}
export function uninstallRuntimeRepository(repository: string): string[] {
  const records = installedProfiles();
  const removed = records
    .filter((record) => record.repository === repository)
    .map((record) => record.runtimeId);
  localStorage.setItem(
    "yomiri_installed_profiles",
    JSON.stringify(records.filter((record) => record.repository !== repository)),
  );
  removed.forEach(unregisterDynamicSource);
  removed.forEach((id) => {
    void sourceExtensionTransport.unregisterSource(id).catch(() => undefined);
  });
  return removed;
}
export const declarativeSources = [
  new MangaDex(mangaDexProfile, sourceHttpClient),
  new DbNewHope(dbNewHopeProfile, sourceHttpClient),
  ...[leerProfile, kazokuProfile, anzProfile, marmotaProfile].map(
    (profile) => new DeclarativeSource(profile, sourceHttpClient),
  ),
];
